import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSupabaseClient } from '../utils/supabase/client';

type Subscription = { ready: boolean; listeners: Set<() => void>; reconnectListeners: Set<() => void>; channel: RealtimeChannel | null };
const subscriptions = new Map<string, Subscription>();
const closing = new Map<string, Promise<unknown>>();

/** One private connection per actor; messages invalidate grants, never authorize them. */
export function subscribeToNavigationChanges(userId: string, listener: () => void, onReconnect: () => void = listener): () => void {
  let subscription = subscriptions.get(userId);
  if (!subscription) {
    subscription = { ready: false, listeners: new Set(), reconnectListeners: new Set(), channel: null };
    subscriptions.set(userId, subscription);
    subscription.listeners.add(listener);
    subscription.reconnectListeners.add(onReconnect);
    const current = subscription;
    const refresh = () => {
      if (subscriptions.get(userId) === current) current.listeners.forEach(callback => callback());
    };
    const connect = () => {
      if (subscriptions.get(userId) !== current || current.listeners.size === 0) return;
      current.channel = getSupabaseClient()
        .channel(`navigation-permissions:${userId}`, { config: { private: true } })
        .on('broadcast', { event: 'changed' }, refresh)
        .subscribe(status => {
          current.ready = status === 'SUBSCRIBED';
          // Recheck on connection/reconnection, closing the initial fetch-to-subscribe gap.
          if (status === 'SUBSCRIBED' && subscriptions.get(userId) === current) current.reconnectListeners.forEach(callback => callback());
        });
    };
    // realtime-js reuses channels by topic. Never attach to one still closing.
    const previousClose = closing.get(userId);
    if (previousClose) void previousClose.then(connect, connect);
    else connect();
  } else {
    subscription.listeners.add(listener);
    subscription.reconnectListeners.add(onReconnect);
  }
  const current = subscription;
  return () => {
    current.listeners.delete(listener);
    current.reconnectListeners.delete(onReconnect);
    if (current.listeners.size === 0 && subscriptions.get(userId) === current) {
      // React may immediately remount in Strict Mode; keep that connection alive.
      queueMicrotask(() => {
        if (current.listeners.size !== 0 || subscriptions.get(userId) !== current) return;
        subscriptions.delete(userId);
        if (current.channel) {
          const pending = getSupabaseClient().removeChannel(current.channel);
          closing.set(userId, pending);
          const done = () => { if (closing.get(userId) === pending) closing.delete(userId); };
          void pending.then(done, done);
        }
      });
    }
  };
}

/** Whether the shared protected channel has joined; this never grants access. */
export function isNavigationSubscriptionReady(userId: string): boolean {
  return subscriptions.get(userId)?.ready ?? false;
}
